import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Edit, Trash2, Image as ImageIcon, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';
import ImageUpload from '../../../components/ImageUpload';

const PAGES = [
  { id: 'churches', name: 'Kyrkor', description: 'Banner for the Churches page' },
  { id: 'news', name: 'Nyheter', description: 'Banner for the News page' },
  { id: 'events', name: 'Kungörelser', description: 'Banner for the Events page' },
  { id: 'bible_readings', name: 'Bibelläsningar', description: 'Banner for the Bible Readings page' },
  { id: 'photo_gallery', name: 'Fotogalleri', description: 'Banner for the Photo Gallery page' },
  { id: 'contact', name: 'Kontakt', description: 'Banner for the Contact page' },
  { id: 'caritas', name: 'Caritas', description: 'Banner for the Caritas page' },
  { id: 'trosundervisning', name: 'Trosundervisning', description: 'Banner for the Trosundervisning page' },
  { id: 'ungdomar', name: 'Ungdomar', description: 'Banner for the Ungdomar page' },
  { id: 'fordjupning', name: 'Fördjupning', description: 'Banner for the Fordjupning page' },
  { id: 'medlemskap', name: 'Medlemskap', description: 'Banner for the Medlemskap page' },
  { id: 'begravning', name: 'Begravning', description: 'Banner for the Begravning page' },
  { id: 'kallelse', name: 'Kallelse', description: 'Banner for the Kallelse page' },
  { id: 'priest-corner', name: 'Prästens lilla hörna', description: 'Banner for the Priest Corner page' },
];

const PageBannersManager = () => {
  const [banners, setBanners] = useState({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: { sv: '', en: '' },
    subtitle: { sv: '', en: '' },
    image_url: '',
    active: true,
  });

  const loadBanners = async () => {
    try {
      const data = await api.getPageBanners();
      const bannerMap = {};
      data.forEach(banner => {
        bannerMap[banner.page_id] = banner;
      });
      setBanners(bannerMap);
    } catch (error) {
      console.error('Error loading banners:', error);
    }
  };

  useEffect(() => {
    loadBanners();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.updatePageBanner(editingPage, formData);
      toast.success('Banner sparad!');
      setIsDialogOpen(false);
      resetForm();
      loadBanners();
    } catch (error) {
      toast.error('Ett fel uppstod: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDelete = async (pageId) => {
    if (window.confirm('Är du säker på att du vill ta bort denna banner?')) {
      try {
        await api.deletePageBanner(pageId);
        toast.success('Banner raderad!');
        loadBanners();
      } catch (error) {
        toast.error('Ett fel uppstod');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      title: { sv: '', en: '' },
      subtitle: { sv: '', en: '' },
      image_url: '',
      active: true,
    });
    setEditingPage(null);
  };

  const handleEdit = (pageId) => {
    const banner = banners[pageId];
    setEditingPage(pageId);
    if (banner) {
      setFormData({
        title: banner.title || { sv: '', en: '' },
        subtitle: banner.subtitle || { sv: '', en: '' },
        image_url: banner.image_url || '',
        active: banner.active !== undefined ? banner.active : true,
      });
    } else {
      // Reset form data but keep editingPage
      setFormData({
        title: { sv: '', en: '' },
        subtitle: { sv: '', en: '' },
        image_url: '',
        active: true,
      });
    }
    setIsDialogOpen(true);
  };

  const getPageName = (pageId) => {
    const page = PAGES.find(p => p.id === pageId);
    return page ? page.name : pageId;
  };

  return (
    <div className="space-y-6" data-testid="page-banners-manager">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Sidbannrar</h1>
          <p className="text-gray-600 mt-1">Hantera bannerbilder för varje sida</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {PAGES.map((page) => {
          const banner = banners[page.id];
          const hasCustomBanner = !!banner;
          
          return (
            <Card key={page.id} className={`overflow-hidden ${!hasCustomBanner ? 'border-dashed border-2' : ''}`}>
              <CardContent className="p-0">
                {/* Banner Preview */}
                <div className="relative h-40 bg-gray-100">
                  {banner?.image_url ? (
                    <img
                      src={banner.image_url}
                      alt={banner.title?.sv || page.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ImageIcon className="w-12 h-12" />
                    </div>
                  )}
                  
                  {/* Status Badge */}
                  <div className="absolute top-2 right-2">
                    {hasCustomBanner ? (
                      banner.active ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Aktiv
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inaktiv
                        </span>
                      )
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Standard
                      </span>
                    )}
                  </div>
                </div>

                {/* Card Info */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 mb-1">{page.name}</h3>
                  {banner?.title?.sv ? (
                    <p className="text-sm text-gray-600 truncate">{banner.title.sv}</p>
                  ) : (
                    <p className="text-sm text-gray-400 italic">Ingen anpassad banner</p>
                  )}
                  
                  <div className="flex space-x-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(page.id)}
                      className="flex-1"
                      data-testid={`edit-banner-${page.id}`}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      {hasCustomBanner ? 'Redigera' : 'Lägg till'}
                    </Button>
                    {hasCustomBanner && (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDelete(page.id)}
                        data-testid={`delete-banner-${page.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {banners[editingPage] ? 'Redigera' : 'Lägg till'} banner för {getPageName(editingPage)}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="banner-form">
            <div>
              <label className="block text-sm font-medium mb-2">Titel (Svenska) *</label>
              <Input
                required
                value={formData.title.sv}
                onChange={(e) => setFormData({ ...formData, title: { ...formData.title, sv: e.target.value } })}
                placeholder="T.ex. Våra kyrkor"
                data-testid="banner-title-sv"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Titel (English)</label>
              <Input
                value={formData.title.en}
                onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                placeholder="e.g. Our Churches"
                data-testid="banner-title-en"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Underrubrik (Svenska)</label>
              <Input
                value={formData.subtitle.sv}
                onChange={(e) => setFormData({ ...formData, subtitle: { ...formData.subtitle, sv: e.target.value } })}
                placeholder="Valfri underrubrik"
                data-testid="banner-subtitle-sv"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Underrubrik (English)</label>
              <Input
                value={formData.subtitle.en}
                onChange={(e) => setFormData({ ...formData, subtitle: { ...formData.subtitle, en: e.target.value } })}
                placeholder="Optional subtitle"
                data-testid="banner-subtitle-en"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Bannerbild *</label>
              <ImageUpload
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                aspectRatio="banner"
                placeholder="Klicka eller dra för att ladda upp bannerbild"
                data-testid="banner-image-upload"
              />
              <p className="text-xs text-gray-500 mt-2">Eller ange URL manuellt:</p>
              <Input
                type="url"
                value={formData.image_url}
                onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                placeholder="https://images.unsplash.com/..."
                className="mt-1"
                data-testid="banner-image-url"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="banner-active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="rounded"
                data-testid="banner-active"
              />
              <label htmlFor="banner-active" className="text-sm font-medium">Aktiv</label>
            </div>
            <Button type="submit" className="w-full" data-testid="banner-submit">
              Spara Banner
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PageBannersManager;
